import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {BaseStepComponent} from '../../../../../shared/components/wizard/base-step.component';
import {debounceTime, of} from 'rxjs';
import {Fluid} from 'primeng/fluid';
import {TranslocoDirective} from '@jsverse/transloco';
import {RadioButton} from 'primeng/radiobutton';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormFieldComponent} from '../../../../../shared/components/inputs/form-field/form-field.component';
import {FormStepValidationService} from '../../../../../shared/services/form-step-validation.service';
import {ProjectWizardService} from '../services/project-wizard.service';
import {ProjectInformationStepData} from '../project-wizard.types';

@Component({
  selector: 'app-project-wizard-general-information-step',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    Fluid,
    TranslocoDirective,
    RadioButton,
    FormFieldComponent,
  ],
  templateUrl: './project-wizard-general-information-step.component.html',
})
export class ProjectWizardGeneralInformationStepComponent
  extends BaseStepComponent<ProjectInformationStepData>
  implements OnInit
{
  override readonly stepIndex = 1;

  private readonly fb = inject(FormBuilder);
  private readonly projectWizardService = inject(ProjectWizardService);
  private readonly formStepValidationService = inject(FormStepValidationService);

  protected readonly yearsInScopeOptions = Array.from({length: 5}, (_, i) => i + 1);

  readonly form = this.fb.group({
    name: this.fb.control('', {validators: Validators.required, updateOn: 'blur'}),
    description: ['', Validators.required],
    yearsInScope: [5],
  });

  readonly nameControl = this.form.get('name') as FormControl<string>;
  readonly descriptionControl = this.form.get('description') as FormControl<string>;

  private readonly isTypingSignal = signal(false);
  protected readonly isCheckingValidationSignal = signal(false);

  protected readonly isValidComputed = computed(() => {
    const formValid = this.form.valid;
    const isChecking = this.isCheckingValidationSignal();
    const isTyping = this.isTypingSignal();
    return formValid && !isChecking && !isTyping;
  });

  constructor() {
    super();
    this.setupValidation();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeFormData();
  }

  private initializeFormData(): void {
    if (this.formData?.name) {
      this.form.patchValue({
        name: this.formData.name,
        description: this.formData.description,
        yearsInScope: this.formData.yearsInScope ?? 5,
      });
    }
  }

  private setupValidation(): void {
    this.setupControlTouchValidation();
    this.setupTypingIndicator();
    this.setupNameExistsCheck();
    this.setupFormValueChanges();
  }

  private setupControlTouchValidation(): void {
    this.formStepValidationService.setupControlTouchValidation(this.nameControl);
    this.formStepValidationService.setupControlTouchValidation(this.descriptionControl);
  }

  private setupTypingIndicator(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.isTypingSignal.set(true);
    });

    this.form.valueChanges.pipe(debounceTime(500), takeUntilDestroyed()).subscribe(() => {
      this.isTypingSignal.set(false);
    });
  }

  private setupNameExistsCheck(): void {
    this.formStepValidationService.attributeValueExists(
      this.nameControl,
      this.isCheckingValidationSignal,
      this.checkProjectName.bind(this),
      this.handleCheckResult.bind(this),
    );
  }

  private checkProjectName(name: string) {
    const trimmed = name?.trim();
    if (this.projectWizardService.isEdit() && this.formData?.name?.trim() === trimmed) {
      return of({exists: false});
    }
    return trimmed
      ? this.projectWizardService.checkProjectNameExists(trimmed)
      : of({exists: false});
  }

  private handleCheckResult(result: any): void {
    if (result.success) {
      this.updateControlErrors(result.response?.exists);
      this.updateStepData();
    } else {
      this.nameControl.setErrors({
        apiError: 'Unable to verify project name. Please try again later.',
      });
    }

    this.isCheckingValidationSignal.set(false);
  }

  private updateControlErrors(exists: boolean): void {
    this.formStepValidationService.updateControlErrors(this.nameControl, exists);
    this.formStepValidationService.updateControlErrors(this.descriptionControl);
  }

  private updateStepData(): void {
    const description = this.descriptionControl.value?.trim();
    const projectName = this.nameControl.value?.trim();

    if (projectName && description && !this.form.errors) {
      this.projectWizardService.updateStepData({
        stepIndex: this.stepIndex,
        data: {
          name: projectName,
          description: description,
          yearsInScope: this.form.get('yearsInScope')?.value || 5,
        },
      });
    }
  }

  private setupFormValueChanges(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.form.valid && !this.isCheckingValidationSignal()) {
        this.updateStepData();
      }
    });
  }
}
